/*
 * Copyright (c) 2005-2024, Paycheck Planner
 * All rights reserved
 * 
 */

import { Categories } from "./categories.js";

import { cleanDate, formatDate } from './date-helpers.js';

import * as utils from "./utils.js";



// TODO - make constant file
const MODEL_START = "ModelStart";
const MODEL_END = "ModelEnd";
const PRIMARY_BIRTHDAY = "PrimaryBirthday";
const SECONDARY_BIRTHDAY = "SecondaryBirthday";


const GROUPTYPE_CASH = 0
const GROUPTYPE_CREDIT = 1
const GROUPTYPE_LOAN = 2
const GROUPTYPE_INVEST = 3
const GROUPTYPE_ASSET = 4

const groupTypes = [
  { group: GROUPTYPE_CASH, desc: "Cash", isAsset: true },
  { group: GROUPTYPE_CREDIT, desc: "Credit", isAsset: false },
  { group: GROUPTYPE_LOAN, desc: "Loans", isAsset: false },
  { group: GROUPTYPE_INVEST, desc: "Investment", isAsset: true },
  { group: GROUPTYPE_ASSET, desc: "Assets", isAsset: true },
];


const ACCTTYPE_BANK = 0;
const ACCTTYPE_CREDITCARD = 1;
const ACCTTYPE_LINEOFCREDIT = 2;
const ACCTTYPE_LOAN = 3;
const ACCTTYPE_MORTGAGE = 4;
const ACCTTYPE_INVESTMENT = 5;


const accountTypes = [
  { type: ACCTTYPE_BANK, group: GROUPTYPE_CASH, code: "BNK", endpoint: "", desc: "Bank Account" },
  { type: ACCTTYPE_CREDITCARD, group: GROUPTYPE_CREDIT, code: "CC", endpoint: "", desc: "Credit Card" },
  { type: ACCTTYPE_LINEOFCREDIT, group: GROUPTYPE_CREDIT, code: "LC", endpoint: "", desc: "Line Of Credit" },
  { type: ACCTTYPE_LOAN, group: GROUPTYPE_LOAN, code: "LOAN", endpoint: "", desc: "Loan" },
  { type: ACCTTYPE_MORTGAGE, group: GROUPTYPE_LOAN, code: "MORT", endpoint: "", desc: "Mortgage" },
  { type: ACCTTYPE_INVESTMENT, group: GROUPTYPE_INVEST, code: "INV", endpoint: "", desc: "Investment" },
];


const ITEMTYPE_INCOME = 1;
const ITEMTYPE_EXPENSE = 2;
const ITEMTYPE_TRANSFER = 3;




export const MM = {};

MM.GROUPTYPE_CASH = GROUPTYPE_CASH
MM.GROUPTYPE_CREDIT = GROUPTYPE_CREDIT
MM.GROUPTYPE_LOAN = GROUPTYPE_LOAN
MM.GROUPTYPE_INVEST = GROUPTYPE_INVEST
MM.GROUPTYPE_ASSET = GROUPTYPE_ASSET

MM.ACCTTYPE_BANK = ACCTTYPE_BANK;
MM.ACCTTYPE_CREDITCARD = ACCTTYPE_CREDITCARD;
MM.ACCTTYPE_LINEOFCREDIT = ACCTTYPE_LINEOFCREDIT;
MM.ACCTTYPE_INVESTMENT = ACCTTYPE_INVESTMENT;

MM.ITEMTYPE_INCOME = ITEMTYPE_INCOME;
MM.ITEMTYPE_EXPENSE = ITEMTYPE_EXPENSE;
MM.ITEMTYPE_TRANSFER = ITEMTYPE_TRANSFER;



MM.getAccountGroupTypes = function () {
  return groupTypes;
}


MM.getAccountCodeFromType = function (type) {
  return accountTypes[type].code;
}


MM.getAcctTypeAsConst = function (code) {
  var num_types = accountTypes.length;

  var ret = -1;

  for (let i = 0; i < num_types; i++) {
    var t = accountTypes[i];

    if (utils.stringCompare(t.code, code))
      return t.type;
  }

  if (ret === -1) {
    throw "Invalid Account Type";
  }

  return ret;
}


MM.getAcctGroupFromAcctType = function (type) {
  const group = accountTypes[type].group;

  return groupTypes[group];
}


// TODO - Might make item type the constant and skip all the conversion, but still need
// and equivalent for URL extraction of type
/**
 * Given a string representation of item type: Income | Expense | Transfer
 * returns its internal constant.
 * Throw exception if type is unknown 
 * @param {string} type 
 */
MM.getItemTypeAsConst = function (type) {
  let clean_type = type.toLowerCase();
  let ret = -1;

  if (clean_type === "income")
    ret = ITEMTYPE_INCOME;
  else if (clean_type === "expense")
    ret = ITEMTYPE_EXPENSE;
  else if (clean_type === "transfer")
    ret = ITEMTYPE_TRANSFER;

  if (ret === -1) {
    throw "Invalid Item Type";
  }

  return ret;
}


MM.createBankAccount = function (model) {
  let acct = MM.createAccount(model);
  acct.type = MM.getAccountCodeFromType(ACCTTYPE_BANK);

  return acct;
}


MM.findMilestoneByID = function(model, id) {
  let clean_id = -1;
  if ( typeof id === "number") {
    clean_id = id;
  } else if ( typeof id === "string") {
    clean_id = parseInt(id);
  }

  let milestone = model.milestones.find( a => a.id === clean_id);

  if (milestone === undefined) {
    throw "Bad milestone id";
  }

  return milestone;
}


MM.findPrimaryAccountID = function (model) {
  let idx = model.accounts.findIndex(a => a.isPrimary === true);

  if (idx === -1) {
    throw "Missing Primary Account";
  }

  return model.accounts[idx].id;
}


MM.findAccountByID = function(model, id) {
  let clean_id = -1;
  if ( typeof id === "number") {
    clean_id = id;
  } else if ( typeof id === "string") {
    clean_id = parseInt(id);
  }

  let acct = model.accounts.find( a => a.id === clean_id);

  if (acct === undefined) {
    throw "Bad account id";
  }

  return acct;
}


MM.createMilestone = function (model) {
  let milestone = {
    label: "",
    expr: "#2020-06-01", // TODO 
    hidden: false,
  };

  addBaseProperties(model, milestone, "milestone", "New Milestone");

  return milestone;
}


MM.addMilestone = function (model, milestone) {
  model.milestones.push(milestone);
}


MM.updateMilestone = function (model, milestone) {
  model.milestones = model.milestones.map(
    m => {
      if (m.id === milestone.id) {
        return { ...m, ...milestone };
      }

      return m;
    }
  );
}


MM.deleteMilestone = function (model, id) {
  let milestones = model.milestones.filter(i => i.id !== id);
  model.milestones = milestones;
}


MM.createLineOfCredit = function (model) {
  let primary = model.accounts.find(a => a.isPrimary === true); // Always one in valid model

  let acct = MM.createAccount(model);
  acct.type = MM.getAccountCodeFromType(ACCTTYPE_LINEOFCREDIT);

  acct.serviceAccountID = primary.id;

  return acct;
}


MM.createCreditCard = function (model) {
  let acct = MM.createAccount(model);
  acct.type = MM.getAccountCodeFromType(ACCTTYPE_CREDITCARD);

  return acct;
}


MM.createInvestment = function (model) {
  let acct = MM.createAccount(model);
  acct.type = MM.getAccountCodeFromType(ACCTTYPE_INVESTMENT);

  return acct;
}


MM.createAccount = function (model) {
  let account = {
    isPrimary: false,
    showInGraph: true,
    disableInGraph: false,

    type: "",
    balance: "0",

    isTaxable: false,

    interest: "0",
    limit: "0",
    threshold: "0",
    taxrate: "0",
    
    serviceAccountID: 0,
    serviceDay: 1,

    lastUpdated: new Date()
  };

  addBaseProperties(model, account, "account", "New Account");

  return account;
}


MM.addAccount = function (model, account) {
  model.accounts.push(account);
}


MM.updateAccount = function (model, acct) {
  model.accounts = model.accounts.map(
    a => {
      if (a.id === acct.id) {
        return { ...a, ...acct };
      }

      return a;
    }
  );
}


MM.createNetIncome = function (model) {
  let item = MM.createItem(model, "Income", "Income", "Net Pay");
  return item;
}


MM.createExpense = function (model, catCode, desc) {
  let item = MM.createItem(model, "Expense", catCode, desc);
  return item;
}


MM.createTransfer = function (model) {
  let item = MM.createItem(model, "Transfer", "", "");
  return item;
}


MM.createItem = function (model, type, catCode, desc) {
  let sub_type = '';
  if (type === 'Transfer') {
    sub_type = 'Fixed';
  }


  // TODO - make this a random start date a few days in the future, possibly even has the model
  // evolves, it makes better guesses.
  let today = new Date();
  today.setDate(today.getDate() + 12);


  let primary_id = MM.findPrimaryAccountID(model);

  let item = {
    itemType: type,
    subType: sub_type,
    active: true,
    category: catCode,

    srcActID: primary_id,
    dstActID: primary_id,

    amount: "0",
    freq: "Monthly",

    startExpr: formatDate(today),
    stopExpr: MODEL_END

  };

  let final_desc = desc.length > 0 ? desc : "New " + type;
  addBaseProperties(model, item, type, final_desc);

  return item;
}


MM.addItem = function (model, item) {
  model.items.push(item);
}


MM.updateItem = function (model, item) {
  model.items = model.items.map(
    i => {
      if (i.id === item.id) {
        return { ...i, ...item };
      }

      return i;
    }
  );
}


MM.deleteItem = function (model, id) {
  let items = model.items.filter(i => i.id !== id);
  model.items = items;
}

/**
 * Given a model, return a list of N items, that have not been used so far, that
 * the editor can present for quick add tools
 * @param {Model} model
 */
MM.populateAddItemList = function (model, type) {
  let items = [];

  function createItem(longCode, desc) {
    let item = {
      code: longCode,
      desc,
      i16: ""
    };

    return item;
  }


  function addItem(code) {
    let cat = Categories.findCategory(code);

    if (cat === null) {
      throw "Invalid Category";
    }

    let item = createItem(code, cat.desc);

    items.push(item);
  }


  if (type === ITEMTYPE_INCOME) {
    addItem("Income");
  } else if (type === ITEMTYPE_EXPENSE) {
    addItem("General");

    addItem("Cell");
    addItem("Rent");
    addItem("Groceries");
    addItem("Cable");
    addItem("Gas");
  } else if (type === ITEMTYPE_TRANSFER) {
    let i = createItem("", "Transfer");
    items.push(i);
  }

  return items;
}


function addBaseProperties(model, item, name, desc) {
  let last_id = getLastID(model);
  let next_id = last_id + 2;

  item.id = next_id;
  item.name = makeUniqueName(name, next_id);
  item.desc = desc;
}


function getLastID(model) {
  var id = 0;

  function listMax(list) {
    if (list.length === 0) {
      return 0;
    }

    let max = list.reduce((max, val) => max.id > val.id ? max : val).id; // // https://stackoverflow.com/questions/1669190/find-the-min-max-element-of-an-array-in-javascript
    return max;
  }


  id = Math.max(id, listMax(model.params));
  id = Math.max(id, listMax(model.milestones));
  id = Math.max(id, listMax(model.accounts));
  id = Math.max(id, listMax(model.assets));
  id = Math.max(id, listMax(model.loans));
  id = Math.max(id, listMax(model.items));

  return id;
}


function makeUniqueName(prefix, id) {
  let name = prefix + id.toString();

  return name;
}

//==========================================================================================================
//==========================================================================================================

class ModelManager {
  constructor() {
    this.lastID = 0;

    //this.symbolTree = {};
  }


  findPrimaryAccountID(model) {
    var id = 0;
    var num_accts = model.accounts.length;

    for (var i = 0; i < num_accts; i++) {
      var acct = model.accounts[i];

      if (acct.isPrimary === true) {
        id = acct.id;
        break;
      }
    }

    return id;
  }


  findModelStartMilestone(model) {
    var num_milestones = model.milestones.length;

    for (var i = 0; i < num_milestones; i++) {
      var milestone = model.milestones[i];

      if (utils.stringCompare(milestone.name, MODEL_START) === true) {
        return milestone;
      }
    }

    return null;
  }


  findMilestoneByID(model, id) {
    return this.findEntityByID(model.milestones, id);
  }


  findAccountByID(model, id) {
    return this.findEntityByID(model.accounts, id);
  }


  findLoanByID(model, id) {
    return this.findEntityByID(model.loans, id);
  }


  findItemByID(model, id) {
    return this.findEntityByID(model.items, id);
  }


  findAssetByID(model, id) {
    return this.findEntityByID(model.assets, id);
  }


  /**
   * Given an account id, return its description or empty string in case its invalid
   * @param {Model} model 
   * @param {number} id
   * @returns {string} 
   */
  getAccountDescByID(model, id) {
    const acct = this.findAccountByID(model, id);
    if (acct !== null) {
      return acct.desc;
    }

    return "";
  }


  /**
   * Returns a list of available milestones in a model to be used for lists and dropdowns
   * @param {*} model 
   */
  getAvailableMilestones(model) {
    var milestones = [];

    var num_milestones = model.milestones.length;

    for (var i = 0; i < num_milestones; i++) {
      var milestone = model.milestones[i];

      var m = {
        name: milestone.name
      }

      milestones.push(m);
    }

    return milestones;
  }


  /**
   * Returns a list of available accounts in a model to be used for lists and dropdowns
   * @param {*} model 
   */
  getAvailableAccounts(model) {
    var accounts = [];

    var num_accts = model.accounts.length;

    for (var i = 0; i < num_accts; i++) {
      var acct = model.accounts[i];

      var a = {
        id: acct.id,
        desc: acct.desc
      }

      accounts.push(a);
    }

    return accounts;
  }


  // Convert parameters to an object to hide complexity for now
  getModelProperties(model) {
    var props = {
      version: model.version,
      name: model.name,

      pbday: this.getParamValue(model, PRIMARY_BIRTHDAY),
      plife: this.getParamValue(model, "PrimaryLifeExpectancy"),
      pret: this.getParamValue(model, "PrimaryRetireAge"),

      sbday: this.getParamValue(model, SECONDARY_BIRTHDAY),
      slife: this.getParamValue(model, "SecondaryLifeExpectancy"),
      sret: this.getParamValue(model, "SecondaryRetireAge"),

    };

    return props;
  }


  toggleItemActiveFlag(model, id) {

    var num_items = model.items.length;

    for (var i = 0; i < num_items; i++) {
      var item = model.items[i];

      if (item.id === id) {
        item.active = !item.active;
        break;
      }
    }

  }

  //--------------------------------------------------------------------------------
  //--------------------------------------------------------------------------------

  createModelParam(model) {
    var param = {
      id: 0,
      paramType: "Date",
      name: "",
      desc: "",

      val: ""
    };

    return param;
  }

  //--------------------------------------------------------------------------------

  createModelMilestone(model) {
    var milestone = {
      hidden: false,
      label: "",

      expr: ""
    };

    this.addBaseProperties(milestone, "milestone", "New Milestone");

    return milestone;
  }


  addModelMilestone(model, milestone) {
    model.milestones.push(milestone);
  }


  updateModelMilestone(model, milestone) {
    var item_count = model.milestones.length;
    for (var i = 0; i < item_count; i++) {
      if (model.milestones[i].id === milestone.id) {
        model.milestones[i] = milestone;
      }
    }
  }

  //--------------------------------------------------------------------------------

  createModelAccount(model) {
    var account = {
      isPrimary: false,
      showInGraph: true,
      disableInGraph: false,

      type: MM.getAccountCodeFromType(MM.ACCTTYPE_BANK),
      balance: "0",

      interest: "0",
      limit: "0",
      threshold: "0",

      serviceAccountID: 0,
      serviceDay: 1,

      lastUpdated: new Date()
    };

    this.addBaseProperties(account, "account", "New Account");

    return account;
  }


  addModelAccount(model, account) {
    var new_model = Object.assign({}, model);

    new_model.accounts = new_model.accounts.slice();
    new_model.accounts.push(account);

    return new_model;
  }


  updateModelAccount(model, account) {
    const new_model = Object.assign({}, model);

    new_model.accounts = new_model.accounts.map(
      acct => {
        if (acct.id === account.id) {
          return account;
        }

        return acct;
      }
    );

    return new_model;
  }

  //--------------------------------------------------------------------------------
  //--------------------------------------------------------------------------------
  //                               Loans
  //--------------------------------------------------------------------------------
  //--------------------------------------------------------------------------------

  createLoan(model, type) {

    if (type === "mortgage") {
      return this.createModelLoan(model, type);
    }

    return null;
  }


  createModelLoan(model, type) {
    var loan = {
      loanType: type,

      srcActID: 0,

      originalBalance: "0",

      currentBalance: "",    // Current balance as of curBalDate
      curBalDate: "",

      interestRate: "0",
      insurance: "0",        // Monthly insurance payment
      propertyTax: "0",      // Monthly property tax payment
      term: 10,              // Duration of mortgage in years
      mortgagePayment: "",   // Mortgage portion of the payment     
    }

    this.addBaseProperties(loan, "loan", "New Mortgage");
    this.addRangeProperties(loan);

    loan.srcActID = this.findPrimaryAccountID(model);

    return loan;
  }


  addModelLoan(model, loan) {
    var new_model = Object.assign({}, model);

    new_model.loans = new_model.loans.slice();
    new_model.loans.push(loan);

    return new_model;
  }


  updateModelLoan(model, loan) {
    const new_model = Object.assign({}, model);

    new_model.loans = new_model.loans.map(
      ln => {
        if (ln.id === loan.id) {
          return loan;
        }

        return ln;
      }
    );

    return new_model;
  }

  //--------------------------------------------------------------------------------

  createModelItem(model, type) {
    var sub_type = '';
    if (type === 'Transfer') {
      sub_type = 'Fixed';
    }

    let primary = this.findPrimaryAccountID(model);

    var item = {
      itemType: type,
      subType: sub_type,
      active: true,
      category: "General",

      srcActID: primary,
      dstActID: primary,

      amount: 0,
      freq: "Monthly",
    };

    this.addBaseProperties(item, type, "New " + type);
    this.addRangeProperties(item);

    return item;
  }


  addModelItem(model, item) {
    var new_model = Object.assign({}, model);

    new_model.items = new_model.items.slice();
    new_model.items.push(item);

    return new_model;
  }


  updateModelItem(model, item) {
    var new_model = Object.assign({}, model);

    new_model.items = new_model.items.map(
      i => {
        if (i.id === item.id) {
          return item;
        }

        return i;
      }
    );

    return new_model;
  }


  deleteModelItem(model, id) {
    var new_model = Object.assign({}, model);

    return new_model;
  }

  //--------------------------------------------------------------------------------

  createAsset(type) {

    if (type === "house") {
      return this.createModelAsset(null, type);
    }

    return null;
  }


  createModelAsset(model, type) {
    var asset = {
      assetType: type,

      originalValue: "0",     // Original cost/price of asset
      yearBuilt: "0",           // Year is was built
      currentValue: "0",      // Current value, if known, appraisal, black book, etc 
      calculatedValue: "0",   // From a depreciation table, if active
    }

    this.addBaseProperties(asset, "asset", "New " + type);

    return asset;
  }


  addModelAsset(model, asset) {
    var new_model = Object.assign({}, model);

    new_model.assets = new_model.assets.slice();
    new_model.assets.push(asset);

    return new_model;
  }


  updateModelAsset(model, asset) {
    const new_model = Object.assign({}, model);

    new_model.assets = new_model.assets.map(
      ast => {
        if (ast.id === asset.id) {
          return asset;
        }

        return ast;
      }
    );

    return new_model;
  }

  //--------------------------------------------------------------------------------

  createModelScenario() {
    var scenario = {

      baseID: -1,
    }

    this.addBaseProperties(scenario, "scenario", "QuickControl");

    return scenario;
  }


  addModelScenario(model, scenario) {
    var new_model = Object.assign({}, model);

    new_model.scenarios = new_model.scenarios.slice();
    new_model.scenarios.push(scenario);

    return new_model;
  }


  updateModelScenario(model, scenario) {
    const new_model = Object.assign({}, model);

    new_model.scenarios = new_model.scenarios.map(
      scn => {
        if (scn.id === scenario.id) {
          return scenario;
        }

        return scn;
      }
    );

    return new_model;
  }


  deleteModelScenario(model, scenario) {
    const new_model = Object.assign({}, model);

    new_model.scenarios = new_model.scenarios.slice();
    var num_scenarios = new_model.scenarios.length;
    var idx = -1;

    for (let i = 0; i < num_scenarios; i++) {
      const s = new_model.scenarios[i];

      if (s.id === scenario.id) {
        idx = i;
        break;
      }
    }

    //    if ( idx === num_scenarios - 1 ) {
    //      new_model.scenarios.pop();
    //    } else if ( idx != -1 ) {
    new_model.scenarios.splice(idx, 1);
    //    }

    return new_model;
  }

  //--------------------------------------------------------------------------------

  updateModelProperties(model, props) {
    model.name = props.name;

    this.updateParamValue(model, PRIMARY_BIRTHDAY, props.pbday);
    this.updateParamValue(model, "PrimaryLifeExpectancy", props.plife);
    this.updateParamValue(model, "PrimaryRetireAge", props.pret);

    this.updateParamValue(model, SECONDARY_BIRTHDAY, props.sbday);
    this.updateParamValue(model, "SecondaryLifeExpectancy", props.slife);
    this.updateParamValue(model, "SecondaryRetireAge", props.sret);
  }


  getParam(model, name) {
    var param = null;
    var lower = name.toLowerCase();
    var num_params = model.params.length;

    for (var i = 0; i < num_params; i++) {
      param = model.params[i];

      if (lower === param.name.toLowerCase()) {
        return param;
      }
    }

    return null;
  }


  getMilestone(model, name) {
    var milestone = null;
    var lower = name.toLowerCase();
    var num_milestones = model.milestones.length;

    for (var i = 0; i < num_milestones; i++) {
      milestone = model.milestones[i];

      if (lower === milestone.name.toLowerCase()) {
        break;
      }
    }

    return milestone;
  }


  getParamValue(model, name) {
    var param = this.getParam(model, name);
    if (param === null)
      return null;

    return param.val;
  }


  addModelParam(model, param) {
    param.id = this.getNextID();
    model.params.push(param);
  }


  updateParamValue(model, name, val) {
    var param = this.getParam(model, name);

    param.val = val;
  }


  /**
   * Called by createEmptyModel only
   * 
   * @param {*} model 
   * @param {*} type 
   * @param {*} name 
   * @param {*} val 
   */
  createAndAddParam(model, type, name, val) {
    var p = this.createModelParam(model);

    p.paramType = type;
    p.name = name;
    p.val = val;

    this.addModelParam(model, p);
  }


  /**
   * Called by createEmptyModel only
   * 
   * @param {*} model 
   * @param {*} hidden 
   * @param {*} name 
   * @param {*} expr 
   */
  createAndAddMilestone(model, hidden, name, expr) {
    var m = this.createModelMilestone(model);

    m.hidden = hidden;
    m.name = name;
    m.expr = expr;

    this.addModelMilestone(model, m);
  }


  findEntityByID(entities, id) {
    var entity = entities.find(e => e.id === id);

    return entity === undefined ? null : entity;
  }


  addBaseProperties(item, name, desc) {
    var next_id = this.getNextID();

    item.id = next_id;
    item.name = this.makeUniqueName(name, next_id);
    item.desc = desc;
  }


  addRangeProperties(item) {
    var start_date = cleanDate("", 0);

    item.startExpr = formatDate(start_date);

    item.stopExpr = MODEL_END;
  }


  getNextID() {
    this.lastID++;
    return this.lastID;
  }


  setLastID(model) {
    var id = 0;

    var param_count = model.params.length;

    for (let i = 0; i < param_count; i++) {
      if (model.params[i].id > id)
        id = model.params[i].id;
    }

    var milestone_count = model.milestones.length;

    for (let i = 0; i < milestone_count; i++) {
      if (model.milestones[i].id > id)
        id = model.milestones[i].id;
    }

    var account_count = model.accounts.length;

    for (let i = 0; i < account_count; i++) {
      if (model.accounts[i].id > id)
        id = model.accounts[i].id;
    }

    var asset_count = model.assets.length;

    for (let i = 0; i < asset_count; i++) {
      if (model.assets[i].id > id)
        id = model.assets[i].id;
    }

    var loan_count = model.loans.length;

    for (let i = 0; i < loan_count; i++) {
      if (model.loans[i].id > id)
        id = model.loans[i].id;
    }

    var item_count = model.items.length;

    for (let i = 0; i < item_count; i++) {
      if (model.items[i].id > id)
        id = model.items[i].id;
    }

    this.lastID = id;
  }


  makeUniqueName(prefix, id) {
    var name = prefix + id.toString();

    return name;
  }

}


export const model_mgr = new ModelManager();

